import { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { IotConfigResponse, iotConfigUseTab, IotTabFormData, UnionIotConfigUseTab } from "src/api/iot/iot-config-type";
import { IoTDeviceRegistration, IotDisplayTab, RegisteredDeviceQueryParams } from "src/api/iot/iot-types";
import { Modal, PageMeta } from "src/api/public-types";
import { BaseInput } from "src/components";
import { BaseButton } from "src/components/BaseButton";
import EditCancelButton from "src/components/EditCancelButton";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import IotCommonTableModal from "src/pages/building/forms/iotDevice/components/IotCommonTableModal";
import { PagePath } from "src/pages/product/details";
import { useModal } from "src/recoil/modalState/hook";
import { createIotConfigColumns, iotTabColumns } from "../../columns/IotConfigColumns";
import { useIotTab } from "../hooks/useIotTab";
import AssignedIotList from "./AssignedIotList";
import IotUseTabControl from "./IotUseTabControl";

type FormData = {
  iotTabList: IotTabFormData[];
};
const queryData: RegisteredDeviceQueryParams = { page: 0, size: 20, sort: { orders: [{ direction: "ASC", property: "internalDisplayName" }] } };

type Props = {
  iotDisplayConfig?: IotConfigResponse;
  iotDisplayTabList: IotDisplayTab[];
  onChangeIotDisplayUseTab: (useTabs: UnionIotConfigUseTab[]) => Promise<void>;
  onUpdateAssignDevices: (registeredDeviceIds: string[], row: any, type?: "assign" | "delete") => Promise<void>;
  onAddIotCustomTab: (tab: IotTabFormData) => Promise<void>;
  onDeleteCustomTab: (groupId: string) => Promise<void>;
};

const IotUseTabSetting = ({
  onChangeIotDisplayUseTab,
  iotDisplayConfig,
  iotDisplayTabList,
  onUpdateAssignDevices,
  onAddIotCustomTab,
  onDeleteCustomTab,
}: Props) => {
  const { setBaseModal, setAbstractModalZ1 } = useModal();
  const location = useLocation();
  const [openModal, setOpenModal] = useState<Modal>({ isOpen: false });
  const [iotDevices, setIotDevices] = useState<IoTDeviceRegistration[]>([]);
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const [isSearched, setIsSearched] = useState(false);
  const [searchParams, setSearchParams] = useState<RegisteredDeviceQueryParams>(queryData);

  // 편집 모드 관리 - 각 탭의 편집 상태를 관리
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const { isAuthority, fetchAuthorityReadonly } = usePartnerAuthority();

  const showAccessDeniedAlert = (type: "w" | "d") => {
    if (!isAuthority(type)) {
      fetchAuthorityReadonly(true);
      return true;
    }
    return false;
  };

  // pathname을 사용하여 컬럼 생성

  // 탭 활성화 상태 확인
  const isDefaultTabEnabled = useMemo(() => iotDisplayConfig?.useTabs.includes(iotConfigUseTab.DEFAULT_TAB) || false, [iotDisplayConfig]);
  const isCustomTabEnabled = useMemo(() => iotDisplayConfig?.useTabs.includes(iotConfigUseTab.CUSTOM_TAB) || false, [iotDisplayConfig]);

  // API 데이터를 Form 데이터로 변환 (id → originalId)
  const transformForForm = (data: IotDisplayTab[]): IotTabFormData[] => {
    return data.map((item) => ({
      ...item,
      originalId: String(item.id || ""), // 기존 id를 originalId로 보관
      id: "", // useFieldArray가 새로운 id 생성하도록 빈 문자열
    }));
  };

  // useForm과 useFieldArray 설정
  const { control, setValue, watch } = useForm<FormData>({
    defaultValues: {
      iotTabList: transformForForm(iotDisplayTabList),
    },
  });

  const { fields, prepend, remove } = useFieldArray({
    control,
    name: "iotTabList",
  });

  const { getIotDeviceToRegister } = useIotTab();

  useEffect(() => {
    setValue("iotTabList", transformForForm(iotDisplayTabList));
  }, [iotDisplayTabList, setValue]);

  useEffect(() => {
    if (openModal.isOpen && openModal.type === "iot") {
      fetchIotDevice(searchParams);
    }
  }, [openModal, searchParams]);

  const handleToggleChange = async (tabValue: UnionIotConfigUseTab, checked: boolean) => {
    if (showAccessDeniedAlert("w")) return;

    const newUseTabs = checked
      ? [...(iotDisplayConfig?.useTabs || []), tabValue]
      : (iotDisplayConfig?.useTabs || []).filter((tab) => tab !== tabValue);

    await onChangeIotDisplayUseTab(newUseTabs);
  };

  // 커스텀 탭 추가
  const handleAddCustomTab = () => {
    prepend({
      type: iotConfigUseTab.CUSTOM_TAB,
      name: "",
      id: "",
      originalId: "", // 새로 추가되는 탭은 originalId 없음
      registeredDeviceIds: [],
    });
  };

  // 순서 변경 (위로)
  const handleMoveUp = async (index: number) => {
    if (showAccessDeniedAlert("w")) return;
    if (index > 0) {
      const currentTab = fields[index] as IotTabFormData & { id: string };
      const targetTab = fields[index - 1] as IotTabFormData & { id: string }; // 위쪽 탭
      // 현재 탭의 priority를 위쪽 탭의 priority로 변경
      const updatedTab = {
        ...currentTab,
        priority: targetTab.priority,
      };

      await onAddIotCustomTab(updatedTab);
    }
  };

  // 순서 변경 (아래로)
  const handleMoveDown = async (index: number) => {
    if (showAccessDeniedAlert("w")) return;
    if (index < fields.length - 1) {
      const currentTab = fields[index] as IotTabFormData & { id: string };
      const targetTab = fields[index + 1] as IotTabFormData & { id: string }; // 아래쪽 탭
      // 현재 탭의 priority를 아래쪽 탭의 priority로 변경
      const updatedTab = {
        ...currentTab,
        priority: targetTab.priority,
      };

      await onAddIotCustomTab(updatedTab);
    }
  };

  // 커스텀 탭들만 필터링하여 순서 버튼 비활성화 조건 계산
  const getCustomTabInfo = (index: number) => {
    const customTabIndices = fields
      .map((field, idx) => ({ field: field as IotTabFormData & { id: string }, index: idx }))
      .filter(({ field }) => field.type === iotConfigUseTab.CUSTOM_TAB)
      .map(({ index }) => index);

    const customTabPosition = customTabIndices.indexOf(index);
    const isFirstCustomTab = customTabPosition === 0;
    const isLastCustomTab = customTabPosition === customTabIndices.length - 1;

    return { isFirstCustomTab, isLastCustomTab };
  };

  // IoT 기기 필드 목록 불러오기
  const fetchIotDevice = async (passParams?: RegisteredDeviceQueryParams) => {
    const { data } = await getIotDeviceToRegister(passParams || {});
    setPageMeta(data.meta.pageMeta);
    setIotDevices(data.data.content);

    // iot 기기 모달 > 기기 chekced > 검색으로 페이지 재호출시 기존 체크박스 해제
    setIsSearched(false);
    return data.data.content;
  };

  const openAssignedDeviceModal = async (assignedGroupId: string) => {
    const assignedList = await fetchIotDevice({ assignedGroupId });
    const deleteAssignedDevice = (registeredDeviceId: string) => {
      if (showAccessDeniedAlert("d")) return;

      setBaseModal({
        isOpen: true,
        type: "iot",
        title: "삭제하시겠습니까?",
        btnLeftTitle: "취소",
        btnRightTitle: "확인",
        onClick: async () => {
          await onUpdateAssignDevices([registeredDeviceId], assignedGroupId, "delete");
          await openAssignedDeviceModal(assignedGroupId);
          setBaseModal({ isOpen: false });
        },
        onClose: () => setBaseModal({ isOpen: false }),
      });
    };

    setAbstractModalZ1({
      isOpen: true,
      type: "iot",
      children: (
        <AssignedIotList
          list={assignedList || []}
          deleteAssignedDevice={deleteAssignedDevice}
          onClose={() => setAbstractModalZ1({ isOpen: false })}
        />
      ),
    });
  };

  // 탭 관리 > 커스텀 탭 row 삭제
  const handleDeleteCustomTab = async (tab: IotTabFormData, index: number) => {
    if (tab.originalId) {
      setBaseModal({
        isOpen: true,
        type: "iot",
        title: "삭제하시겠습니까?",
        children: (
          <p>
            탭을 삭제하면 해당 탭에 등록한 <br />
            IoT 기기도 노출되지 않습니다.
          </p>
        ),
        btnLeftTitle: "취소",
        btnRightTitle: "확인",
        onClick: () => {
          onDeleteCustomTab(tab.originalId!);
          setBaseModal({ isOpen: false });
        },
        onClose: () => setBaseModal({ isOpen: false }),
      });
    } else {
      remove(index);
    }
  };

  // 편집 모드 시작
  const handleStartEdit = (tabId: string) => {
    setEditingTabId(tabId);
  };

  // 탭 수정 저장
  const handleSaveEdit = async (tab: IotTabFormData, index: number) => {
    const tabName = watch(`iotTabList.${index}.name`);

    if (!tabName?.trim()) {
      return;
    }

    try {
      // 수정된 탭 데이터 생성
      const updatedTab = {
        ...tab,
        name: tabName,
      };
      await onAddIotCustomTab(updatedTab);

      // 편집 모드 종료
      setEditingTabId(null);
    } catch (error) {
      console.error("탭 수정 실패:", error);
    }
  };

  return (
    <>
      <div className="contents-container__scroll">
        {" "}
        <div className="contents-container__wrap ">
          <IotUseTabControl iotDisplayConfig={iotDisplayConfig} handleToggleChange={handleToggleChange} />
          <article className="contents-container__wrap-article">
            <div className="flex-center-between">
              <div className="contents-container__sub-title">
                <h2>탭 관리</h2>
              </div>
              {isAuthority("w") && <BaseButton title="+커스텀 탭 추가" disabled={!isCustomTabEnabled} onClick={handleAddCustomTab} />}
            </div>
            <table className="inner-table" width={iotTabColumns.reduce((acc, column) => acc + column.width, 0)}>
              <thead className="border-y">
                <tr className="text-left">
                  {iotTabColumns.map((column) => (
                    <th key={column.Header} className={`bg-white py15 px10`} style={{ width: column.width }}>
                      <span>{column.Header}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fields.map((field, index: number) => {
                  const tab = field as IotTabFormData & { id: string };
                  const { isFirstCustomTab, isLastCustomTab } = getCustomTabInfo(index);

                  // 현재 탭의 활성화 상태 확인
                  const isCurrentTabEnabled = tab.type === iotConfigUseTab.DEFAULT_TAB ? isDefaultTabEnabled : isCustomTabEnabled;

                  return (
                    <tr key={field.id}>
                      {/* 순서 */}
                      <td className="">
                        {/* 커스텀 탭일 때만 순서 버튼 표시 */}

                        {tab.type === iotConfigUseTab.CUSTOM_TAB && tab.originalId && (
                          <div className="flex-center " style={{ height: 30 }}>
                            <BaseButton //
                              title=""
                              className={`base-btn ic-chevron-up mr2 cursor-pointer color-white pa0 h30 size-small`}
                              disabled={isFirstCustomTab || !isCurrentTabEnabled}
                              onClick={() => {
                                handleMoveUp(index);
                              }}
                            />
                            <BaseButton //
                              title=""
                              className={`base-btn ic-chevron-down  cursor-pointer color-white pa0 h30`}
                              disabled={isLastCustomTab || !isCurrentTabEnabled}
                              onClick={() => {
                                handleMoveDown(index);
                              }}
                            />
                          </div>
                        )}
                      </td>
                      <td className="">
                        {/* 탭 아이디 */}
                        <div className={`pl10 ${!isCurrentTabEnabled ? "text-primary4" : ""}`}>{tab.originalId}</div>
                      </td>
                      <td className="">
                        {/* 탭 종류 */}
                        <div className={`pl10 ${!isCurrentTabEnabled ? "text-primary4" : ""}`}>
                          {tab.type === iotConfigUseTab.CUSTOM_TAB ? "커스텀 탭" : "기본 탭"}
                        </div>
                      </td>
                      <td>
                        {/* 탭 명 */}
                        {tab.originalId || tab.type === iotConfigUseTab.DEFAULT_TAB ? (
                          // 기존 탭이거나 기본 탭인 경우
                          editingTabId === tab.originalId ? (
                            // 편집 모드 - Input으로 변경
                            <Controller
                              control={control}
                              name={`iotTabList.${index}.name`}
                              render={({ field: { onChange, value, name } }) => (
                                <BaseInput
                                  className="minmax300 pl10"
                                  value={value || ""}
                                  maxLength={16}
                                  onChange={onChange}
                                  name={name}
                                  placeholder="탭 명을 입력해주세요."
                                  disabled={!isCurrentTabEnabled}
                                />
                              )}
                            />
                          ) : (
                            // 일반 모드 - 텍스트로 표시
                            <div className={`pl10 ${!isCurrentTabEnabled ? "text-primary4" : ""}`}>{tab.name}</div>
                          )
                        ) : (
                          // 새로 추가된 탭 (originalId 없음) - 항상 Input
                          <Controller
                            control={control}
                            name={`iotTabList.${index}.name`}
                            render={({ field: { onChange, value, name } }) => (
                              <BaseInput
                                className="minmax300 pl10"
                                value={value || ""}
                                onChange={onChange}
                                name={name}
                                maxLength={16}
                                placeholder="탭 명을 입력해주세요."
                                disabled={!isCurrentTabEnabled}
                              />
                            )}
                          />
                        )}
                      </td>
                      {/* 기기 연결 개수 */}
                      <td>
                        <div className="pl10 ">
                          {tab.type === iotConfigUseTab.CUSTOM_TAB && (
                            <span
                              className={`${tab.registeredDeviceIds?.length ? "text-underline cursor-pointer text-blue pa4" : ""}`}
                              onClick={() => {
                                openAssignedDeviceModal(tab.originalId!);
                              }}
                            >
                              {tab.registeredDeviceIds?.length && tab.registeredDeviceIds?.length > 0 ? tab.registeredDeviceIds?.length : "-"}
                            </span>
                          )}
                          {tab.type === iotConfigUseTab.DEFAULT_TAB && (
                            <span
                              className={`${tab.registeredDevices?.length ? "text-underline cursor-pointer text-blue pa4" : ""}`}
                              onClick={() => {
                                if (tab.registeredDevices?.length === 0) return;

                                setAbstractModalZ1({
                                  isOpen: true,
                                  type: "iot",
                                  children: (
                                    <AssignedIotList
                                      list={tab.registeredDevices || []}
                                      onClose={() => setAbstractModalZ1({ isOpen: false })}
                                      type={iotConfigUseTab.DEFAULT_TAB}
                                    />
                                  ),
                                });
                              }}
                            >
                              {tab.registeredDevices?.length && tab.registeredDevices?.length > 0 ? tab.registeredDevices?.length : "-"}
                            </span>
                          )}
                        </div>
                      </td>
                      {/* 기기 연결 */}
                      <td className="">
                        {tab.type === iotConfigUseTab.CUSTOM_TAB && tab.originalId && (
                          <div className="pl10">
                            <BaseButton
                              title="설정"
                              className="color-white"
                              disabled={!isCurrentTabEnabled}
                              onClick={() => {
                                if (showAccessDeniedAlert("w")) return;
                                setOpenModal({ isOpen: true, type: "iot", payload: tab });
                              }}
                            />
                          </div>
                        )}
                      </td>
                      <td>
                        {tab.type === iotConfigUseTab.CUSTOM_TAB && (
                          <div className="pl10">
                            {tab.originalId ? (
                              // 기존 탭 - 수정/저장 버튼 토글
                              editingTabId === tab.originalId ? (
                                <BaseButton
                                  title="저장"
                                  disabled={!isCurrentTabEnabled}
                                  onClick={() => {
                                    if (!watch(`iotTabList.${index}.name`)?.trim()) {
                                      return;
                                    }
                                    setBaseModal({
                                      isOpen: true,
                                      type: "iot",
                                      title: "저장하시겠습니까?",
                                      btnLeftTitle: "취소",
                                      btnRightTitle: "확인",
                                      onClick: () => {
                                        // 여기서 실제 수정 API 호출
                                        handleSaveEdit(tab, index);
                                        setBaseModal({ isOpen: false });
                                      },
                                      onClose: () => setBaseModal({ isOpen: false }),
                                    });
                                  }}
                                />
                              ) : (
                                <BaseButton
                                  title="수정"
                                  className="color-white"
                                  disabled={!isCurrentTabEnabled}
                                  onClick={() => {
                                    if (showAccessDeniedAlert("w")) return;
                                    handleStartEdit(tab.originalId!);
                                  }}
                                />
                              )
                            ) : (
                              // 새로 추가된 탭 - 저장 버튼 (기존 동작 유지)
                              <BaseButton
                                title="저장"
                                disabled={!isCurrentTabEnabled}
                                onClick={() => {
                                  if (!watch(`iotTabList.${index}.name`)?.trim()) {
                                    return;
                                  }
                                  setBaseModal({
                                    isOpen: true,
                                    type: "iot",
                                    title: "저장하시겠습니까?",
                                    btnLeftTitle: "취소",
                                    btnRightTitle: "확인",
                                    onClick: () => {
                                      onAddIotCustomTab(watch(`iotTabList.${index}`));
                                      setBaseModal({ isOpen: false });
                                    },
                                    onClose: () => setBaseModal({ isOpen: false }),
                                  });
                                }}
                              />
                            )}
                          </div>
                        )}
                      </td>
                      {/* 삭제 */}
                      <td>
                        {tab.type === iotConfigUseTab.CUSTOM_TAB && (
                          <div className="pl10">
                            <button
                              className="base-trash-btn color-gray border-none"
                              disabled={!isCurrentTabEnabled}
                              onClick={() => {
                                if (showAccessDeniedAlert("d")) return;
                                handleDeleteCustomTab(tab, index);
                              }}
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </article>
          {openModal.isOpen && (
            <IotCommonTableModal
              title="IoT 기기 설정"
              checkboxType="checkbox"
              pageMeta={pageMeta}
              isSearched={isSearched}
              addedList={(openModal.payload as IotTabFormData)?.registeredDeviceIds || []}
              currentSize={(size: number) => {
                setSearchParams({ ...searchParams, size, page: 0 });
              }}
              goToPage={(page) => {
                setSearchParams({ ...searchParams, page });
              }}
              columns={createIotConfigColumns(location.pathname).slice(0, 5)}
              data={iotDevices}
              onClick={(selected: IoTDeviceRegistration[]) => {
                const register = selected.map((item) => ({
                  registeredDeviceId: String(item.platformDevice.registeredDeviceId || ""),
                }));

                onUpdateAssignDevices(
                  register.map((item) => item.registeredDeviceId),
                  (openModal.payload as IotTabFormData).originalId,
                );
                setIsSearched(false);
              }}
              onClose={() => {
                setOpenModal({ isOpen: false });
                setSearchParams(queryData);
              }}
            />
          )}
        </div>
      </div>
      <div className="contents-container__btn-wrap">
        <div className="left-area"></div>
        <div className="right-area">
          <EditCancelButton to={PagePath.iotConfig.list} title="수정취소" />
        </div>
      </div>
    </>
  );
};

export default IotUseTabSetting;
